import { type FC, useId } from "react"
import styled, { css } from "styled-components"

import { CheckmarkIcon } from "~/components/Icon"
import LoadingIndicator from "~/components/LoadingIndicator"
import { componentBackgroundColor } from "~/utilities/styles"

// Every checkbox needs a name: a visible label, or an accessible name when the context already labels it visually.
type Props = (
	| { label: string; ariaLabel?: never }
	| {
			label?: undefined
			/** Accessible name for checkboxes without a visible label. */
			ariaLabel: string
	  }
) & {
	isChecked: boolean
	onChange?(isChecked: boolean): void
	isBold?: boolean
	isDisabled?: boolean
	isLoading?: boolean
	className?: string
}
const Checkbox: FC<Props> = ({ label, ariaLabel, isChecked, onChange, isBold, isDisabled, isLoading, className }) => {
	const id = useId()

	return (
		<div className={className} style={{ position: "relative" }}>
			<Input
				type="checkbox"
				id={id}
				checked={isChecked}
				onChange={event => onChange?.(event.target.checked)}
				disabled={isDisabled}
				aria-busy={isLoading}
				aria-label={label === undefined ? ariaLabel : undefined}
			/>
			<Label htmlFor={id}>
				<Box aria-hidden>{isLoading ? <StyledLoadingIndicator /> : <StyledCheckmarkIcon />}</Box>
				{label !== undefined && <Text $isBold={isBold}>{label}</Text>}
			</Label>
		</div>
	)
}

type CheckboxProps = {
	label?: string
	/** Shared by every radio in a group, so the browser treats them as one control. */
	name?: string
	isChecked: boolean
	onChange?(isChecked: boolean): void
	isBold?: boolean
	className?: string
}
export const Radio: FC<CheckboxProps> = ({ label, name, isChecked, onChange, isBold, className }) => {
	const id = useId()

	return (
		<div className={className} style={{ position: "relative" }}>
			<Input
				type="radio"
				id={id}
				name={name}
				checked={isChecked}
				onChange={event => onChange?.(event.target.checked)}
			/>
			<Label htmlFor={id}>
				<Circle aria-hidden>
					<DotIcon />
				</Circle>
				{label !== undefined && <Text $isBold={isBold}>{label}</Text>}
			</Label>
		</div>
	)
}

const Label = styled.label`
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
`

const boxStyles = css`
	display: block;
	width: 20px;
	height: 20px;
	background-color: white;
	border: 1px solid #d0d5dd;
	transition: 0.07s;

	&:active {
		background-color: ${componentBackgroundColor};
	}
`

const Box = styled.div`
	${boxStyles}
	padding: 2px;
	border-radius: 4px;
`

const Circle = styled.div`
	${boxStyles}
	padding: 5px;
	border-radius: 50%;
`

const StyledCheckmarkIcon = styled(CheckmarkIcon)`
	scale: 0;
	width: 100%;
	height: 100%;
	color: ${props => props.theme.primaryColor};
	transition: 0.07s;
`

const DotIcon = styled.div`
	scale: 0;
	width: 100%;
	height: 100%;
	background-color: ${props => props.theme.primaryColor};
	border-radius: 50%;
`

const StyledLoadingIndicator = styled(LoadingIndicator)`
	width: 100%;
	height: 100%;
	padding: 4px;
`

// Visually hidden rather than display: none, so the native control stays focusable and announced.
const Input = styled.input`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	overflow: hidden;
	clip-path: inset(50%);
	white-space: nowrap;
	border: 0;

	&:focus-visible ~ ${Label} ${Box}, &:focus-visible ~ ${Label} ${Circle} {
		outline: 2px solid ${props => props.theme.primaryColor};
		outline-offset: 2px;
	}

	&:checked ~ ${Label} ${StyledCheckmarkIcon}, &:checked ~ ${Label} ${DotIcon} {
		scale: 1;
	}

	&:disabled ~ ${Label} {
		cursor: default;
	}

	&:disabled ~ ${Label} ${Box}, &:disabled ~ ${Label} ${Circle} {
		background-color: #f5f7f9;

		${StyledCheckmarkIcon} {
			color: #d0d5dd;
		}
		${DotIcon} {
			background-color: #d0d5dd;
		}
	}
`

const Text = styled.span<{ $isBold?: boolean }>`
	font-weight: ${props => (props.$isBold ? "700" : "500")};
	font-size: ${props => (props.$isBold ? "16px" : "14px")};
	letter-spacing: ${props => (props.$isBold ? "0.5px" : "0")};
	user-select: none;
`

export default Checkbox
